import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, ShoppingCart, Factory, Truck, HardHat, Check,
  ChevronRight, Warehouse, QrCode, Building2, Briefcase,
  Calendar, Edit2, Paperclip, Upload, Download, Trash2,
  FileText, Image as ImageIcon, FileSpreadsheet, FileArchive, File as FileIcon,
} from 'lucide-react';
import {
  commandes, etapesCommande, clients,
  formatDate, formatDateTime, formatMontant, formatTaille,
} from '../data/mockData';

const pieceJointeIcon = {
  pdf: FileText,
  image: ImageIcon,
  doc: FileText,
  xls: FileSpreadsheet,
  zip: FileArchive,
};

const pieceJointeBadge = {
  pdf: 'danger',
  image: 'info',
  doc: 'sky',
  xls: 'ok',
  zip: 'warn',
};

const pieceJointeLabel = {
  pdf: 'PDF',
  image: 'Image',
  doc: 'DOC',
  xls: 'XLSX',
  zip: 'ZIP',
};

const etapeIcon = {
  creation: ShoppingCart,
  depart_usine: Factory,
  reception_bl: Truck,
  installation: HardHat,
};

const CommandeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const commande = commandes.find((c) => c.id === id);

  if (!commande) {
    return (
      <div className="page-container">
        <button className="back-link" onClick={() => navigate('/commandes')}>
          <ArrowLeft size={16} /> Retour aux commandes
        </button>
        <div className="empty-state">
          <ShoppingCart size={28} />
          <p>Commande introuvable.</p>
        </div>
      </div>
    );
  }

  const etapeIndex = etapesCommande.findIndex((e) => e.id === commande.etape);
  const client = clients.find((c) => c.id === commande.clientId);
  const totalQte = commande.lignes.reduce((sum, l) => sum + l.quantite, 0);
  const totalSeries = commande.lignes.reduce((sum, l) => sum + l.numerosSerie.length, 0);
  const piecesJointes = commande.piecesJointes || [];
  const totalPoids = piecesJointes.reduce((sum, p) => sum + (p.taille || 0), 0);

  return (
    <div className="page-container">
      <button className="back-link" onClick={() => navigate('/commandes')}>
        <ArrowLeft size={16} /> Retour aux commandes
      </button>

      <div className="page-header">
        <div>
          <div className="page-title-row">
            <div className="page-icon-wrapper">
              <ShoppingCart size={22} />
            </div>
            <div>
              <h1 className="page-title mono">{commande.id}</h1>
              <p className="page-subtitle">
                {commande.fournisseurNom} - Commande du {formatDate(commande.date)}
              </p>
            </div>
          </div>
        </div>
        <div className="page-actions">
          <span className={`badge badge-${commande.etape === 'installation' ? 'ok' : 'info'}`}>
            {etapesCommande.find((e) => e.id === commande.etape)?.label}
          </span>
          {piecesJointes.length > 0 && (
            <span className="badge badge-sky">
              <Paperclip size={12} /> {piecesJointes.length} piece(s) jointe(s)
            </span>
          )}
          <button className="btn-ghost"><Edit2 size={14} /> Modifier</button>
        </div>
      </div>

      <div className="card">
        <header className="card-header">
          <div className="card-title">
            <ShoppingCart size={18} />
            <h2>Cycle de la commande</h2>
          </div>
        </header>
        <div className="card-body">
          <div className="stepper">
            {etapesCommande.map((etape, idx) => {
              const Icon = etapeIcon[etape.id];
              const done = idx < etapeIndex;
              const active = idx === etapeIndex;
              return (
                <div key={etape.id} className={`step ${done ? 'done' : ''} ${active ? 'active' : ''}`}>
                  <div className="step-bubble">
                    {done ? <Check size={16} /> : <Icon size={16} />}
                  </div>
                  <div className="step-body">
                    <strong>{etape.label}</strong>
                    <span>{etape.description}</span>
                  </div>
                  {idx < etapesCommande.length - 1 && <ChevronRight size={16} className="step-arrow" />}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="kpi-grid kpi-grid-4" style={{ marginTop: '1.25rem' }}>
        <div className="kpi-card">
          <div className="kpi-icon"><ShoppingCart size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Lignes</span>
            <span className="kpi-value">{commande.lignes.length}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><Warehouse size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Quantite totale</span>
            <span className="kpi-value">{totalQte}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><QrCode size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">N series traces</span>
            <span className="kpi-value">{totalSeries}</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon"><Calendar size={22} /></div>
          <div className="kpi-body">
            <span className="kpi-label">Montant total HT</span>
            <span className="kpi-value">{formatMontant(commande.montantHT)}</span>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ marginTop: '1.25rem' }}>
        <div className="card">
          <header className="card-header">
            <div className="card-title">
              <Building2 size={18} />
              <h2>Fournisseur</h2>
            </div>
            <button className="btn-link" onClick={() => navigate(`/fournisseurs/${commande.fournisseurId}`)}>
              Voir la fiche <ChevronRight size={14} />
            </button>
          </header>
          <div className="card-body info-grid">
            <div>
              <span className="info-label">Fournisseur</span>
              <span className="info-value">{commande.fournisseurNom}</span>
            </div>
            <div>
              <span className="info-label">Date commande</span>
              <span className="info-value">{formatDate(commande.date)}</span>
            </div>
            <div>
              <span className="info-label">Entrepot destination</span>
              <span className="info-value">{commande.entrepot}</span>
            </div>
            <div>
              <span className="info-label">Etape actuelle</span>
              <span className="info-value">{etapesCommande.find((e) => e.id === commande.etape)?.label}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <header className="card-header">
            <div className="card-title">
              <Briefcase size={18} />
              <h2>Client & chantier</h2>
            </div>
            {client && (
              <button className="btn-link" onClick={() => navigate(`/clients/${client.id}`)}>
                Voir le client <ChevronRight size={14} />
              </button>
            )}
          </header>
          <div className="card-body info-grid">
            <div>
              <span className="info-label">Client (marche)</span>
              <span className="info-value">{client?.nom || '-'}</span>
            </div>
            <div>
              <span className="info-label">Code marche</span>
              <span className="info-value mono">{client?.codeMarche || '-'}</span>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <span className="info-label">Chantier</span>
              <span className="info-value">{commande.chantier}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <Truck size={18} />
            <h2>Bon de livraison</h2>
          </div>
        </header>
        <div className="card-body">
          {commande.bl ? (
            <div className="info-grid">
              <div>
                <span className="info-label">Numero BL</span>
                <span className="info-value mono">{commande.bl.numero}</span>
              </div>
              <div>
                <span className="info-label">Date reception</span>
                <span className="info-value">{formatDate(commande.bl.date)}</span>
              </div>
              <div>
                <span className="info-label">Entrepot destination</span>
                <span className="info-value">{commande.bl.entrepotDestination}</span>
              </div>
            </div>
          ) : (
            <div className="empty-state compact">
              <p>Aucun bon de livraison saisi a ce stade (etape actuelle : <strong>{etapesCommande.find((e) => e.id === commande.etape)?.label}</strong>).</p>
            </div>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <Paperclip size={18} />
            <h2>Pieces jointes ({piecesJointes.length})</h2>
          </div>
          <div className="page-actions">
            {piecesJointes.length > 0 && (
              <span className="meta-line">{formatTaille(totalPoids)} au total</span>
            )}
            <button className="btn-ghost btn-small">
              <Upload size={14} /> Ajouter une piece jointe
            </button>
          </div>
        </header>
        <div className="card-body">
          <div className="dropzone">
            <Upload size={22} />
            <div>
              <strong>Glissez-deposez vos fichiers ici</strong>
              <p className="meta-line">
                PDF, images, XLSX, DOCX, ZIP - 20 Mo max par fichier
              </p>
            </div>
            <button className="btn-ghost btn-small">
              <Paperclip size={14} /> Parcourir
            </button>
          </div>

          {piecesJointes.length === 0 ? (
            <div className="empty-state compact" style={{ marginTop: '1rem' }}>
              <Paperclip size={22} />
              <p>Aucune piece jointe pour cette commande.</p>
              <p className="meta-line">
                Ajoutez le bon de commande signe, le devis fournisseur, l'accuse de reception ou des photos terrain.
              </p>
            </div>
          ) : (
            <ul className="pj-list">
              {piecesJointes.map((pj) => {
                const Icon = pieceJointeIcon[pj.type] || FileIcon;
                const badge = pieceJointeBadge[pj.type] || 'neutral';
                const typeLabel = pieceJointeLabel[pj.type] || 'Fichier';
                return (
                  <li key={pj.id} className="pj-item">
                    <div className={`pj-icon pj-icon-${badge}`}>
                      <Icon size={20} />
                    </div>
                    <div className="pj-body">
                      <div className="pj-head">
                        <strong className="pj-name">{pj.nom}</strong>
                        <span className={`badge badge-${badge}`}>{typeLabel}</span>
                        <span className="chip chip-sky">{pj.categorie}</span>
                      </div>
                      <div className="pj-meta">
                        <span>{formatTaille(pj.taille)}</span>
                        <span>•</span>
                        <span>Ajoute le {formatDateTime(pj.dateAjout)}</span>
                        <span>•</span>
                        <span>Par {pj.ajoutePar}</span>
                      </div>
                    </div>
                    <div className="pj-actions">
                      <button className="btn-ghost btn-small" title="Telecharger">
                        <Download size={14} /> Telecharger
                      </button>
                      <button className="btn-ghost btn-small btn-danger" title="Supprimer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: '1.25rem' }}>
        <header className="card-header">
          <div className="card-title">
            <Warehouse size={18} />
            <h2>Lignes de commande & tracabilite serie</h2>
          </div>
        </header>
        <div className="card-body">
          <table className="data-table clickable">
            <thead>
              <tr>
                <th>Equipement</th>
                <th style={{ textAlign: 'right' }}>Qte</th>
                <th style={{ textAlign: 'right' }}>Prix unit.</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th>Numeros de serie</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {commande.lignes.map((ligne, idx) => (
                <tr
                  key={idx}
                  className="hoverable clickable"
                  onClick={() => ligne.equipementId && navigate(`/equipements/${ligne.equipementId}`)}
                >
                  <td><strong>{ligne.libelle}</strong></td>
                  <td style={{ textAlign: 'right' }}>{ligne.quantite}</td>
                  <td style={{ textAlign: 'right' }}>{formatMontant(ligne.prixUnit)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <strong>{formatMontant(ligne.prixUnit * ligne.quantite)}</strong>
                  </td>
                  <td>
                    {ligne.numerosSerie.length > 0 ? (
                      ligne.numerosSerie.map((ns) => (
                        <span key={ns} className="chip chip-sky">
                          <QrCode size={11} /> {ns}
                        </span>
                      ))
                    ) : (
                      <span className="meta-line">En attente saisie terrain</span>
                    )}
                  </td>
                  <td style={{ width: 28 }}><ChevronRight size={16} className="arrow-muted" /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={3} style={{ textAlign: 'right', fontWeight: 600 }}>Total HT</td>
                <td style={{ textAlign: 'right' }}>
                  <strong className="mono" style={{ fontSize: '1.05em', color: 'var(--sky-700)' }}>
                    {formatMontant(commande.montantHT)}
                  </strong>
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CommandeDetail;
